import SendbirdChat from '@sendbird/chat'
import {
  GroupChannel,
  GroupChannelListOrder,
  GroupChannelListQuery,
  GroupChannelListQueryParams,
  GroupChannelModule,
  SuperChannelFilter,
  UnreadChannelFilter,
} from '@sendbird/chat/groupChannel'
import { BaseMessage, MentionType, UserMessage } from '@sendbird/chat/message'
import { OpenChannelModule } from '@sendbird/chat/openChannel'
import Cookies from 'js-cookie'

import { Env } from 'config/Env'
import { ChatNotificationDetails } from 'features/discussions/types'

import { getFormatedDate } from './getFormatedDate'
import {
  fetchUntilPageHasItemsOrQueryEnds,
  getDiscussionUnreadDisplayCount,
  getDiscussionUnreadMentionCount,
  getSendbirdSuperChannelFilter,
  getSuperGroupFallbackUnreadCount,
  isDiscussionUnread,
  sortChannelsMentionsFirst,
} from './sendbirdSupergroups'

export const groupChannelListenerName = 'GROUP_CHANNEL_LISTENER'
export const openChannelListenerName = 'OPEN_CHANNEL_LISTENER'

export type MetaDataProp = {
  hasAttachment: boolean
  id?: number
  size?: number
  data?: string
}

export type MessageType = {
  state: {
    body?: string
    index: number
    user?: string | null
    timestamp: string
    time: string
    metadata?: MetaDataProp
    messageId: number
    mentionList?: string[] | null
    mentionType?: MentionType | null
    customType?: string
  }
  author: boolean | undefined
}

export const sbChatClient = SendbirdChat.init({
  appId: Env.SENDBIRD_CHAT_ID ?? '',
  modules: [new GroupChannelModule(), new OpenChannelModule()],
})

export const asyncConnectUser = async (userId: string, userSessionToken: string) =>
  new Promise((resolve, reject) => {
    const MAX_RETRIES = 3
    const RETRY_DELAY_MS = 1000
    let retries = 0

    const tryConnect = async () => {
      try {
        const test = await sbChatClient.connect(userId, userSessionToken)

        resolve(test) // Resolve the promise when the connection is successful
      } catch (error) {
        retries += 1 // Increment retries explicitly

        if (retries < MAX_RETRIES) {
          // Wrap tryConnect in a non-async function and use it as the setTimeout callback
          setTimeout(() => {
            tryConnect().catch(reject) // Call tryConnect and handle any errors
          }, RETRY_DELAY_MS)
        } else {
          reject(new Error('Max retry attempts reached. Connection failed.'))
        }
      }
    }

    tryConnect() // Initial connection attempt
  })

export const getTime = (dateVal: string | number | Date) => {
  const currentTime = new Date()
  const tim = new Date(dateVal)
  const isSameDate = currentTime.toDateString() === tim.toDateString()
  const timeDifference = Math.abs(currentTime.getTime() - tim.getTime())

  if (isSameDate && timeDifference <= 3600000) {
    if (Math.round(timeDifference / 60000) === 0) {
      return 'Now'
    }
    return `${Math.round(timeDifference / 60000)} mins ago`
  }

  return getFormatedDate(true, false, tim?.toString())
}

export const getAuthor = (idParam: string, loggedUserVal: string) => idParam === loggedUserVal

export const getMetaData = (strVal: string) => {
  try {
    return JSON.parse(strVal)
  } catch (e) {
    return {
      hasAttachment: false,
      data: strVal,
    }
  }
}

export const getTimeStamp = (dateVal: number) => {
  if (dateVal) {
    return new Date(dateVal)?.toDateString()
  }
  return '0'
}

export const getStructuredMessage = (chatMessList: UserMessage[], reversed = true) => {
  const loggedInuser = Cookies.get('chat_user_id') ?? ''
  const reversedArray = [...chatMessList]
  if (reversed) {
    reversedArray.reverse()
  }
  return reversedArray?.map(item => {
    if (item?.message) {
      return {
        state: {
          body: item?.message,
          index: 1,
          user: item?.sender?.nickname,
          timestamp: getTimeStamp(item?.createdAt),
          time: getTime(item?.createdAt),
          metadata: getMetaData(item?.data),
          messageId: item?.messageId,
          mentionList: item?.mentionedUserIds,
          mentionType: item?.mentionType,
          customType: item?.customType,
        },
        author: getAuthor(item?.sender?.userId, loggedInuser),
      }
    }
    return null
  })
}

export type MetaDataType = {
  facility_name?: string
  referral_name?: string
  referral_Id?: string
  patient_id: number
  patient_facility_id?: number
}

export const getFacilityName = (cData: unknown) => {
  const changedData = cData as MetaDataType
  return changedData?.facility_name
}

export const getReferralId = (cData: unknown) => {
  const changedData = cData as MetaDataType
  return changedData?.referral_Id
}
export const getPatientId = (cData: unknown) => {
  const changedData = cData as MetaDataType
  return changedData?.patient_facility_id ?? 0
}

export const getPatientReferralId = (cData: unknown) => {
  const changedData = cData as MetaDataType
  return changedData?.patient_id ?? 0
}
export const getChannelName = (cData: unknown) => {
  const changedData = cData as MetaDataType
  return changedData?.referral_name
}

export const getTimeStamps = (lastMessageData?: BaseMessage | null) => {
  const lastMess = lastMessageData as UserMessage
  return getTime(lastMess?.createdAt)
}

const UNREAD_PAGE_SIZE = 20
const SUPER_BACKFILL_PAGE_SIZE = 100
const MESSAGES_DROPDOWN_UNREAD_LIMIT = 50
const UNREAD_REFRESH_DEBOUNCE_MS = 500

const unreadListQueryParams = (
  customFilterArray: string[],
  options?: { channelNameContains?: string; includeEmpty?: boolean; limit?: number },
): GroupChannelListQueryParams => ({
  includeEmpty: options?.includeEmpty ?? true,
  includeFrozen: true,
  limit: options?.limit ?? UNREAD_PAGE_SIZE,
  order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
  customTypesFilter: customFilterArray?.length ? customFilterArray : undefined,
  channelNameContainsFilter: options?.channelNameContains || undefined,
  unreadChannelFilter: UnreadChannelFilter.UNREAD_MESSAGE,
  superChannelFilter: getSendbirdSuperChannelFilter(),
})

const unreadSuperGroupQueryParams = (
  customFilterArray: string[],
  options?: { channelNameContains?: string; limit?: number },
): GroupChannelListQueryParams => ({
  includeEmpty: true,
  includeFrozen: true,
  limit: options?.limit ?? UNREAD_PAGE_SIZE,
  order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
  customTypesFilter: customFilterArray?.length ? customFilterArray : undefined,
  channelNameContainsFilter: options?.channelNameContains || undefined,
  unreadChannelFilter: UnreadChannelFilter.ALL,
  superChannelFilter: SuperChannelFilter.SUPER,
})

export const mergeUnreadChannelLists = (
  sdkUnread: GroupChannel[],
  superUnread: GroupChannel[],
): GroupChannel[] => {
  const byUrl = new Map<string, GroupChannel>()
  ;[...sdkUnread, ...superUnread].forEach(channel => {
    if (channel?.url) {
      byUrl.set(channel.url, channel)
    }
  })
  return sortChannelsMentionsFirst(
    Array.from(byUrl.values()),
    Cookies.get('chat_user_id') ?? '',
  ).filter((channel): channel is GroupChannel => Boolean(channel))
}

/** Keep existing order on load-more. Mentions-first sort is only for the first page. */
export const appendUnreadChannels = (
  existing: GroupChannel[],
  more: GroupChannel[],
): GroupChannel[] => {
  const seen = new Set<string>()
  const appended: GroupChannel[] = []
  ;[...existing, ...more].forEach(channel => {
    if (!channel?.url || seen.has(channel.url)) {
      return
    }
    seen.add(channel.url)
    appended.push(channel)
  })
  return appended
}

const superUnreadByUrl = new Map<string, GroupChannel>()
let lastSdkUnreadCount = 0
let superUnreadBackfillId = 0

const currentUserId = () => Cookies.get('chat_user_id') ?? ''

const trackedSuperFallbackCount = () =>
  getSuperGroupFallbackUnreadCount(Array.from(superUnreadByUrl.values()), currentUserId())

export const getTrackedUnreadBadgeCount = () => lastSdkUnreadCount + trackedSuperFallbackCount()

/** Track Super Groups whose SDK unread counts are 0 so org-wide members are not capped at 200. */
export const noteDiscussionUnreadState = (channel: GroupChannel): void => {
  if (!channel?.url || !channel.isSuper) {
    return
  }
  const userId = currentUserId()
  const sdkUnread = (channel.unreadMessageCount ?? 0) > 0 || (channel.unreadMentionCount ?? 0) > 0
  if (!sdkUnread && isDiscussionUnread(channel, userId)) {
    superUnreadByUrl.set(channel.url, channel)
    return
  }
  superUnreadByUrl.delete(channel.url)
}

export const createUnreadSuperGroupQuery = (
  customFilterArray: string[],
  options?: { channelNameContains?: string; limit?: number },
): GroupChannelListQuery =>
  sbChatClient.groupChannel.createMyGroupChannelListQuery(
    unreadSuperGroupQueryParams(customFilterArray, options),
  )

export const fetchNextUnreadSuperGroupPage = async (
  query: GroupChannelListQuery,
): Promise<GroupChannel[]> => {
  const batch = await query.next()
  const userId = currentUserId()
  ;(batch ?? []).forEach(noteDiscussionUnreadState)
  return (batch ?? []).filter(channel => isDiscussionUnread(channel, userId))
}

/**
 * Super Groups with unreadMessageCount 0 are missing from UNREAD_MESSAGE.
 * First page only; callers keep `superQuery` and page on scroll so org users are not capped.
 */
export const fetchUnreadSuperGroupFallback = async (
  customFilterArray: string[],
  options?: { channelNameContains?: string; maxPages?: number },
): Promise<GroupChannel[]> => {
  if (!currentUserId()) {
    return []
  }
  const query = createUnreadSuperGroupQuery(customFilterArray, {
    channelNameContains: options?.channelNameContains,
  })
  const maxPages = options?.maxPages ?? 1
  const collect = async (page: number, collected: GroupChannel[]): Promise<GroupChannel[]> => {
    if (page >= maxPages) {
      return collected
    }
    const unread = await fetchNextUnreadSuperGroupPage(query)
    if (!unread.length && !query.hasNext) {
      return collected
    }
    return collect(page + 1, [...collected, ...unread])
  }
  return collect(0, [])
}

export const fetchUnreadDiscussionChannels = async (
  customFilterArray: string[],
  options?: { channelNameContains?: string; includeEmpty?: boolean },
): Promise<{
  channels: GroupChannel[]
  query: GroupChannelListQuery
  superQuery: GroupChannelListQuery
}> => {
  const query = sbChatClient.groupChannel.createMyGroupChannelListQuery(
    unreadListQueryParams(customFilterArray, options),
  )
  const superQuery = createUnreadSuperGroupQuery(customFilterArray, {
    channelNameContains: options?.channelNameContains,
  })
  const [sdkPage, superUnread] = await Promise.all([
    query.next(),
    fetchNextUnreadSuperGroupPage(superQuery),
  ])
  return {
    channels: mergeUnreadChannelLists(sdkPage ?? [], superUnread),
    query,
    superQuery,
  }
}

export const hasMoreUnreadDiscussionPages = (
  query: GroupChannelListQuery | null,
  superQuery: GroupChannelListQuery | null,
): boolean => Boolean(query?.hasNext || superQuery?.hasNext)

export const fetchMoreUnreadDiscussionChannels = async (
  query: GroupChannelListQuery | null,
  superQuery: GroupChannelListQuery | null,
): Promise<GroupChannel[]> => {
  const [sdkPage, superPage] = await Promise.all([
    query?.hasNext ? query.next() : Promise.resolve([] as GroupChannel[]),
    superQuery?.hasNext
      ? fetchUntilPageHasItemsOrQueryEnds(
          () => fetchNextUnreadSuperGroupPage(superQuery),
          () => superQuery.hasNext,
        )
      : Promise.resolve([] as GroupChannel[]),
  ])
  return appendUnreadChannels(sdkPage ?? [], superPage ?? [])
}

/**
 * Fill the Messages preview: mentions-first on the first page only, then append.
 * Later pages are not re-sorted so visible rows do not jump. Stops at 50 so the
 * 340px menu does not render every unread thread; View All is the full list.
 */
export const fetchAllUnreadDiscussionChannels = async (
  customFilterArray: string[],
  options?: {
    channelNameContains?: string
    includeEmpty?: boolean
    maxChannels?: number
    onPage?: (channels: GroupChannel[]) => boolean | void
  },
): Promise<GroupChannel[]> => {
  const maxChannels = options?.maxChannels ?? MESSAGES_DROPDOWN_UNREAD_LIMIT
  const query = sbChatClient.groupChannel.createMyGroupChannelListQuery(
    unreadListQueryParams(customFilterArray, {
      channelNameContains: options?.channelNameContains,
      includeEmpty: options?.includeEmpty,
      limit: maxChannels,
    }),
  )
  const superQuery = createUnreadSuperGroupQuery(customFilterArray, {
    channelNameContains: options?.channelNameContains,
    limit: maxChannels,
  })
  const [sdkPage, superUnread] = await Promise.all([
    query.next(),
    fetchNextUnreadSuperGroupPage(superQuery),
  ])
  const capPreview = (channels: GroupChannel[]) =>
    channels.length > maxChannels ? channels.slice(0, maxChannels) : channels
  const channels = capPreview(mergeUnreadChannelLists(sdkPage ?? [], superUnread))
  if (options?.onPage?.(channels) === false) {
    return channels
  }
  const drainRemainingPages = async (collected: GroupChannel[]): Promise<GroupChannel[]> => {
    if (collected.length >= maxChannels || !hasMoreUnreadDiscussionPages(query, superQuery)) {
      return collected
    }
    const more = await fetchMoreUnreadDiscussionChannels(query, superQuery)
    const next = capPreview(appendUnreadChannels(collected, more))
    if (next.length !== collected.length && options?.onPage?.(next) === false) {
      return next
    }
    if (next.length >= maxChannels) {
      return next
    }
    return drainRemainingPages(next)
  }
  return drainRemainingPages(channels)
}

const mapChannelsToNotifications = (channels: GroupChannel[]): ChatNotificationDetails[] => {
  const userId = currentUserId()
  return channels.map(item => ({
    patientName: getChannelName(item?.cachedMetaData) ?? '',
    facilityName: getFacilityName(item?.cachedMetaData) ?? item.name ?? '',
    referralId: getReferralId(item?.cachedMetaData) ?? '',
    timeStamp: getTimeStamps(item?.lastMessage) ?? '',
    threadId: item?.url ?? '',
    patientId: getPatientId(item?.cachedMetaData),
    unReadCount: getDiscussionUnreadDisplayCount(item, userId),
    unreadMentionCount: getDiscussionUnreadMentionCount(item, userId),
  }))
}

const getSdkUnreadMessageCount = (customFilArray: string[]) =>
  sbChatClient.groupChannel.getTotalUnreadMessageCount({
    channelCustomTypesFilter: customFilArray?.length ? customFilArray : undefined,
    superChannelFilter: getSendbirdSuperChannelFilter(),
  })

export const asyncGetUnreadChatList = async (
  setChatNotifFunc: (arg0: ChatNotificationDetails[]) => void,
  customFilterArray: string[],
) => {
  const { channels } = await fetchUnreadDiscussionChannels(customFilterArray)
  setChatNotifFunc(mapChannelsToNotifications(channels))
}

const unreadBadgeCount = async (
  customFilArray: string[],
): Promise<{ count: number; channels: GroupChannel[] }> => {
  const [sdkCount, unread] = await Promise.all([
    getSdkUnreadMessageCount(customFilArray),
    fetchUnreadDiscussionChannels(customFilArray),
  ])
  lastSdkUnreadCount = sdkCount || 0
  unread.channels.forEach(noteDiscussionUnreadState)
  return {
    count: getTrackedUnreadBadgeCount(),
    channels: unread.channels,
  }
}

export const getUnreadChatCountSb = async (
  setUnreadCountFunc: (count: number) => void,
  customFilArray: string[],
) => {
  const { count } = await unreadBadgeCount(customFilArray)
  setUnreadCountFunc(count)
}

export const refreshUnreadDiscussions = async (
  setChatNotifFunc: (arg0: ChatNotificationDetails[]) => void,
  setUnreadCountFunc: (count: number) => void,
  customFilterArray: string[],
) => {
  const { count, channels } = await unreadBadgeCount(customFilterArray)
  setChatNotifFunc(mapChannelsToNotifications(channels))
  setUnreadCountFunc(count)
}

/** SDK total only — Super fallback unreads live in the tracker, not a 200-channel scan. */
export const refreshUnreadBadgeCount = async (
  setUnreadCountFunc: (count: number) => void,
  customFilArray: string[],
) => {
  lastSdkUnreadCount = (await getSdkUnreadMessageCount(customFilArray)) || 0
  setUnreadCountFunc(getTrackedUnreadBadgeCount())
}

/** Walk every Super Group in the background so org-wide membership is not truncated at 200. */
export const backfillSuperGroupUnread = async (
  customFilterArray: string[],
  setUnreadCountFunc: (count: number) => void,
) => {
  superUnreadBackfillId += 1
  const runId = superUnreadBackfillId
  if (!currentUserId()) {
    return
  }
  const query = createUnreadSuperGroupQuery(customFilterArray, {
    limit: SUPER_BACKFILL_PAGE_SIZE,
  })
  const pageNext = async (): Promise<void> => {
    if (runId !== superUnreadBackfillId) {
      return
    }
    await fetchNextUnreadSuperGroupPage(query)
    if (runId !== superUnreadBackfillId) {
      return
    }
    setUnreadCountFunc(getTrackedUnreadBadgeCount())
    if (query.hasNext) {
      await pageNext()
    }
  }
  await pageNext()
}

export const applyLiveDiscussionUnread = (
  channel: GroupChannel,
  setUnreadCountFunc: (count: number) => void,
) => {
  if (!channel?.isSuper) {
    return
  }
  noteDiscussionUnreadState(channel)
  setUnreadCountFunc(getTrackedUnreadBadgeCount())
}

let unreadRefreshTimer: ReturnType<typeof setTimeout> | null = null

export const scheduleUnreadDiscussionRefresh = (
  _setChatNotifFunc: (arg0: ChatNotificationDetails[]) => void,
  setUnreadCountFunc: (count: number) => void,
  customFilterArray: string[],
) => {
  if (unreadRefreshTimer) {
    clearTimeout(unreadRefreshTimer)
  }
  unreadRefreshTimer = setTimeout(() => {
    unreadRefreshTimer = null
    refreshUnreadBadgeCount(setUnreadCountFunc, customFilterArray)
  }, UNREAD_REFRESH_DEBOUNCE_MS)
}

export const getAsyncDisconnectChat = async () => {
  superUnreadBackfillId += 1
  superUnreadByUrl.clear()
  lastSdkUnreadCount = 0
  try {
    Cookies.remove('gcc_chat_token')
    Cookies.remove('chat_user_id')
    await sbChatClient.disconnect()
  } catch {
    Cookies.remove('gcc_chat_token')
    Cookies.remove('chat_user_id')
  }
}

export const retryJoinOpenChannel = async () => {
  const channelId = Env.NOTIFICATION_CHANNEL_ID ?? ''
  const channel = await sbChatClient.openChannel.getChannel(channelId)
  channel.enter()
}
export const asyncJoinOpenChannel = async () => {
  try {
    const channelId = Env.NOTIFICATION_CHANNEL_ID ?? ''
    const channel = await sbChatClient.openChannel.getChannel(channelId)
    channel.enter()
    const toDoListChannelId = Env.TASKLIST_CHANNEL_ID ?? ''
    const toDoChannel = await sbChatClient.openChannel.getChannel(toDoListChannelId)
    toDoChannel.enter()
  } catch (error) {
    retryJoinOpenChannel()
  }
}

/**
 * Metadata every plain text discussion message carries.
 *
 * Kept identical to what the discussion panel sends so a reply written from the Updates
 * feed is indistinguishable from one written in the panel.
 */
const DISCUSSION_TEXT_METADATA = JSON.stringify({ hasAttachment: false, id: '' })

/**
 * Resolves a group channel, reconnecting once if the SDK session has dropped.
 *
 * The app connects Sendbird at start-up, but a long-lived Referral Details page can
 * outlive that session. This mirrors the recovery the chat notification list already
 * performs rather than failing the send outright.
 * @param channelUrl Sendbird group channel url.
 */
const getGroupChannelWithReconnect = async (channelUrl: string): Promise<GroupChannel> => {
  try {
    // Awaited into a local rather than returned directly: a returned rejection would
    // escape this try block and skip the reconnect below.
    const channel = await sbChatClient.groupChannel.getChannel(channelUrl)
    return channel
  } catch (error) {
    const chatUserId = Cookies.get('chat_user_id')
    const chatToken = Cookies.get('gcc_chat_token')

    if (!chatUserId || !chatToken) throw error

    await asyncConnectUser(chatUserId, chatToken)
    return sbChatClient.groupChannel.getChannel(channelUrl)
  }
}

/**
 * Sends a plain text message to a discussion channel.
 *
 * Used by the Updates feed's inline reply box (18298), which posts to the thread without
 * opening the discussion panel. Mentions are not supported from that box — the panel
 * remains the place for those.
 * @param channelUrl Sendbird group channel url.
 * @param message message body; callers are expected to have trimmed it.
 * @returns the sent message, so callers can render it optimistically.
 */
export const sendDiscussionMessage = async (
  channelUrl: string,
  message: string,
): Promise<UserMessage> => {
  const channel = await getGroupChannelWithReconnect(channelUrl)

  return new Promise<UserMessage>((resolve, reject) => {
    channel
      .sendUserMessage({ message, customType: 'text', data: DISCUSSION_TEXT_METADATA })
      .onFailed(error => reject(error))
      .onSucceeded(sentMessage => resolve(sentMessage as UserMessage))
  })
}
